/**
 * HTTP API (server `/api`): Better Auth sessions (email + password, anonymous guests),
 * profile, and the short-lived websocket ticket. Cookies carry the session. Every failure
 * becomes an `ApiError` whose message is already in player words (never "HTTP 500").
 */
const BASE = (import.meta.env.VITE_SERVER_URL as string | undefined)?.replace(/\/$/, '') ?? '';

/** The one line for "the server is not there" (network error, proxy error page, 5xx). */
export const SERVER_DOWN = 'SERVER UNREACHABLE · TRY AGAIN SOON';
/** Minimum password length (the server's Better Auth setting). */
export const MIN_PASSWORD = 8;

export interface Me {
  id: string;
  name: string;
  email: string | null;
  isGuest: boolean;
  /** room the user is currently seated in (reconnect) */
  room: string | null;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }

  /** The server could not be reached (or answered with an error page). */
  get down(): boolean {
    return this.message === SERVER_DOWN;
  }
}

/** Better Auth error codes (and our own) in player words. */
const AUTH_TEXT: Record<string, string> = {
  INVALID_EMAIL_OR_PASSWORD: 'WRONG EMAIL OR PASSWORD',
  INVALID_EMAIL: 'THAT EMAIL DOES NOT LOOK RIGHT',
  INVALID_PASSWORD: 'WRONG EMAIL OR PASSWORD',
  USER_NOT_FOUND: 'WRONG EMAIL OR PASSWORD',
  PASSWORD_TOO_SHORT: `PASSWORD · ${MIN_PASSWORD}+ CHARACTERS`,
  PASSWORD_TOO_LONG: 'PASSWORD · AT MOST 128 CHARACTERS',
  USER_ALREADY_EXISTS: 'AN ACCOUNT WITH THAT EMAIL EXISTS · SIGN IN',
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: 'AN ACCOUNT WITH THAT EMAIL EXISTS · SIGN IN',
  TOO_MANY_REQUESTS: 'TOO MANY TRIES · WAIT A MINUTE',
  badName: 'CALLSIGN · 2–16 LETTERS OR DIGITS',
};

/** The player-facing text of a failed response. */
export function errorText(status: number, body: Record<string, unknown> | null): string {
  if (status === 0 || status >= 500 || !body) return SERVER_DOWN;
  const code = String(body.code ?? body.error ?? '');
  if (AUTH_TEXT[code]) return AUTH_TEXT[code];
  const msg = String(body.message ?? '');
  if (/callsign/i.test(msg)) return AUTH_TEXT.badName;
  if (status === 429) return AUTH_TEXT.TOO_MANY_REQUESTS;
  if (status === 401) return 'SIGNED OUT · SIGN IN AGAIN';
  // something unexpected but readable: short and in the house style
  return msg && msg.length <= 48 ? msg.toUpperCase() : 'SOMETHING WENT WRONG · TRY AGAIN';
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      credentials: 'include',
      ...init,
      headers: { ...(init.body ? { 'Content-Type': 'application/json' } : {}), ...init.headers },
    });
  } catch {
    throw new ApiError(0, SERVER_DOWN);
  }
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (!res.ok) throw new ApiError(res.status, errorText(res.status, body));
  // a 200 without JSON is a proxy's page, not our server
  if (!body) throw new ApiError(502, SERVER_DOWN);
  return body as T;
}

const post = <T>(path: string, data: unknown = {}) => call<T>(path, { method: 'POST', body: JSON.stringify(data) });

export const api = {
  /** The signed-in user, or null (`/api/session`: 200 either way, so a signed-out load logs no error). */
  async me(): Promise<Me | null> {
    try {
      return (await call<{ me: Me | null }>('/api/session')).me;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return null;
      throw e;
    }
  },
  /** Is the server up (and its database)? Never throws. */
  async healthy(): Promise<boolean> {
    try {
      await call<{ ok: boolean }>('/api/health');
      return true;
    } catch {
      return false;
    }
  },
  guest: () => post('/api/auth/sign-in/anonymous'),
  signIn: (email: string, password: string) => post('/api/auth/sign-in/email', { email, password }),
  signUp: (name: string, email: string, password: string) => post('/api/auth/sign-up/email', { name, email, password }),
  signOut: () => post('/api/auth/sign-out'),
  rename: (name: string) => call<{ name: string }>('/api/me', { method: 'PATCH', body: JSON.stringify({ name }) }),
  ticket: () => call<{ ticket: string; expiresAt: number }>('/api/ws-ticket'),
};

/** Websocket URL of the game server. */
export function wsUrl(): string {
  if (BASE) return `${BASE.replace(/^http/, 'ws')}/ws`;
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
}
